import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from '@/pages/ProjectsPage.module.css';

type AudienceInsight = {
  id: string;
  kind: string;
  label_kind: string;
  title: string;
  payload: Record<string, unknown>;
  period_start: string | null;
  period_end: string | null;
  computed_at: string;
};

function FactBadge() {
  return (
    <span
      style={{
        fontSize: '0.75rem',
        padding: '2px 8px',
        borderRadius: 999,
        background: 'var(--color-signal-muted, #ccfbf1)',
        color: 'var(--color-signal, #0f766e)',
        marginLeft: 8,
      }}
    >
      Feit
    </span>
  );
}

function fmtPct(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
}

function fmtNum(v: number | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return new Intl.NumberFormat('nl-NL').format(v);
}

function IntegrationHint({ status }: { status?: string }) {
  if (status === 'active') return null;
  return (
    <p className={styles.mutedSmall}>
      {status === 'missing' ? (
        <>
          Connector niet gekoppeld.{' '}
          <Link to="/app/integraties">Integraties instellen</Link>
        </>
      ) : (
        <>Integratie-status: {status ?? 'onbekend'}. Controleer sync in instellingen.</>
      )}
    </p>
  );
}

function Ga4Card({ insight }: { insight: AudienceInsight }) {
  const p = insight.payload;
  const metrics = p.metrics as
    | {
        sessions?: number;
        conversions?: number;
        engagement_rate?: number;
        active_users?: number;
      }
    | undefined;
  const status = p.integration_status as string | undefined;
  return (
    <article className={styles.card}>
      <h3>
        {insight.title}
        <FactBadge />
      </h3>
      <IntegrationHint status={status} />
      {insight.period_start && insight.period_end ? (
        <p className={styles.mutedSmall}>
          Periode {insight.period_start} t/m {insight.period_end} · {p.snapshot_count as number}{' '}
          dagen met data
        </p>
      ) : null}
      {metrics ? (
        <ul className={styles.list}>
          <li>Sessions: {fmtNum(metrics.sessions)}</li>
          <li>Conversies: {fmtNum(metrics.conversions)}</li>
          <li>
            Engagement rate:{' '}
            {metrics.engagement_rate != null
              ? `${(metrics.engagement_rate * 100).toFixed(1)}%`
              : '—'}
          </li>
          <li>Trend sessions (7d vs vorige 7d): {fmtPct(p.trend_sessions_pct as number)}</li>
        </ul>
      ) : (
        <p className={styles.mutedSmall}>Nog geen GA4-snapshots in deze periode.</p>
      )}
    </article>
  );
}

function GscCard({ insight }: { insight: AudienceInsight }) {
  const p = insight.payload;
  const metrics = p.metrics as
    | {
        clicks?: number;
        impressions?: number;
        ctr?: number;
        position?: number;
        top_queries?: { query: string; clicks: number; impressions: number }[];
      }
    | undefined;
  const status = p.integration_status as string | undefined;
  return (
    <article className={styles.card}>
      <h3>
        {insight.title}
        <FactBadge />
      </h3>
      <IntegrationHint status={status} />
      {insight.period_start && insight.period_end ? (
        <p className={styles.mutedSmall}>
          Periode {insight.period_start} t/m {insight.period_end} (GSC ~3 dagen vertraagd)
        </p>
      ) : null}
      {metrics ? (
        <>
          <ul className={styles.list}>
            <li>Clicks: {fmtNum(metrics.clicks)}</li>
            <li>Impressies: {fmtNum(metrics.impressions)}</li>
            <li>CTR: {metrics.ctr != null ? `${(metrics.ctr * 100).toFixed(2)}%` : '—'}</li>
            <li>Gem. positie: {metrics.position != null ? metrics.position.toFixed(1) : '—'}</li>
            <li>Trend clicks (7d vs vorige 7d): {fmtPct(p.trend_clicks_pct as number)}</li>
          </ul>
          {(metrics.top_queries?.length ?? 0) > 0 ? (
            <>
              <h4>Top queries (som over periode)</h4>
              <ul className={styles.list}>
                {metrics.top_queries!.map((q) => (
                  <li key={q.query}>
                    {q.query} · {q.clicks} clicks
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      ) : (
        <p className={styles.mutedSmall}>Nog geen GSC-snapshots in deze periode.</p>
      )}
    </article>
  );
}

function ScraperCard({ insight }: { insight: AudienceInsight }) {
  const funnel = insight.payload.funnel as Record<string, number> | undefined;
  if (!funnel) return null;
  return (
    <article className={styles.card}>
      <h3>
        {insight.title}
        <FactBadge />
      </h3>
      <ul className={styles.list}>
        <li>Scrape jobs: {funnel.jobs_count}</li>
        <li>Kandidaten: {funnel.discovered}</li>
        <li>Goedgekeurd: {funnel.approved}</li>
        <li>In doelgroep (contacten): {funnel.in_audience_leads}</li>
        <li>Met e-mail: {funnel.with_email}</li>
      </ul>
    </article>
  );
}

export function AudienceInsightsPanel({ audienceId }: { audienceId: string }) {
  const qc = useQueryClient();

  const insightsQ = useQuery({
    queryKey: ['audience-insights', audienceId],
    queryFn: () =>
      apiFetch<{ insights: AudienceInsight[] }>(`/api/audiences/${audienceId}/insights`),
  });

  const refresh = useMutation({
    mutationFn: () =>
      apiFetch<{ insights: AudienceInsight[] }>(`/api/audiences/${audienceId}/insights/refresh`, {
        method: 'POST',
        body: JSON.stringify({ days: 30 }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['audience-insights', audienceId], { insights: data.insights });
    },
  });

  const byKind = new Map((insightsQ.data?.insights ?? []).map((i) => [i.kind, i]));
  const ga4 = byKind.get('ga4');
  const gsc = byKind.get('gsc');
  const scraper = byKind.get('scraper');

  return (
    <section>
      <div className={styles.row} style={{ marginBottom: 'var(--space-md)' }}>
        <p className={styles.mutedSmall} style={{ flex: 1 }}>
          Geaggregeerde metingen van <strong>jouw eigen website</strong> (GA4/GSC) plus scraper-funnel
          voor deze doelgroep. Geen individuele bezoekers — alleen feiten uit snapshots.
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          Vernieuwen (30 dagen)
        </Button>
      </div>

      {insightsQ.isLoading ? <LoadingState /> : null}
      {!insightsQ.isLoading && !(insightsQ.data?.insights ?? []).length ? (
        <StateMessage title="Nog geen inzichten opgeslagen">
          Klik op Vernieuwen om feiten uit integraties en scraper te materialiseren.
        </StateMessage>
      ) : null}

      {ga4 ? <Ga4Card insight={ga4} /> : null}
      {gsc ? <GscCard insight={gsc} /> : null}
      {scraper ? <ScraperCard insight={scraper} /> : null}
    </section>
  );
}
